// The three tables of the Project Overview. Every column header sorts; a row click
// shows the place on the map; Report buttons open the online report at that
// milepost. Column `key`s are the sort keys the PDF export understands.
import React, { useState } from 'react';
import { pct, shortManeuvers } from '../Safety/ManeuverLinks';
import { ANALYSES, ANALYSIS_COLORS, ANALYSIS_NAMES, SEVERITY_RANK, segmentName, sortRows } from './overviewModel';

const fmt2 = (v) => (v == null ? '—' : Number(v).toFixed(2));

export const AnalysisTag = ({ analysis }) => (
  <span className={`ov-an ${analysis === 'GLR' ? 'ov-an-dark' : ''}`} style={{ background: ANALYSIS_COLORS[analysis] }}
    title={ANALYSIS_NAMES[analysis]}>{analysis}</span>
);
const Severity = ({ value }) => <span className={`safety-sev safety-sev-${value}`}>{value}</span>;

const ReportLink = ({ url, label, title }) => (url ? (
  <a className="ov-report" href={url} target="_blank" rel="noreferrer" title={title}
    onClick={(e) => e.stopPropagation()}>{label} ↗</a>
) : <span className="safety-muted">—</span>);

function SortableTable({ columns, rows, sort, onSort, onRowClick, limit, rowKey }) {
  // Pinned rows ("tack") stay on top, in the order they were pinned, whatever the sort
  // and the row limit; a second click releases the row.
  const [pins, setPins] = useState([]);
  const accessors = Object.fromEntries(columns.filter((c) => c.sortValue).map((c) => [c.key, c.sortValue]));
  const byKey = new Map(rows.map((r) => [rowKey(r), r]));
  const pinned = pins.map((k) => byKey.get(k)).filter(Boolean);
  const pinnedKeys = new Set(pins);
  const ordered = sortRows(rows.filter((r) => !pinnedKeys.has(rowKey(r))), sort, accessors);
  const shown = [...pinned, ...(limit > 0 ? ordered.slice(0, limit) : ordered)];
  const togglePin = (key) => setPins((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
  return (
    <>
      <div className="safety-table-scroll">
        <table className={`safety-table ${onRowClick ? 'safety-table-clickable' : ''}`}>
          <thead>
            <tr>
              <th className="ov-pin-col" title="Pin a row to the top of the table">{pins.length ? `${pins.length} pinned` : ''}</th>
              {columns.map((c) => (
                <th key={c.key} className={c.num ? 'ov-num' : ''} title={c.title}>
                  {c.sortValue ? (
                    <button type="button" className="ov-sort" title={`Sort by ${c.label}`}
                      onClick={() => onSort({ key: c.key, dir: sort?.key === c.key ? (sort.dir === 'asc' ? 'desc' : 'asc') : (c.num ? 'desc' : 'asc') })}>
                      {c.label}{sort?.key === c.key && <span className="ov-sort-arrow">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                    </button>
                  ) : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => {
              const key = rowKey(r);
              const isPinned = pinnedKeys.has(key);
              const lastPinned = isPinned && i === pinned.length - 1 && shown.length > pinned.length;
              return (
                <tr key={key} onClick={onRowClick ? () => onRowClick(r) : undefined}
                  className={`${isPinned ? 'ov-pinned' : ''} ${lastPinned ? 'ov-pinned-last' : ''}`}>
                  <td className="ov-pin-col">
                    <button type="button" className={`ov-pin ${isPinned ? 'on' : ''}`} aria-pressed={isPinned}
                      title={isPinned ? 'Release this row' : 'Keep this row on top'}
                      aria-label={isPinned ? 'Release this row' : 'Keep this row on top'}
                      onClick={(e) => { e.stopPropagation(); togglePin(key); }}>📌</button>
                  </td>
                  {columns.map((c) => <td key={c.key} className={c.num ? 'ov-num' : ''}>{c.render(r)}</td>)}
                </tr>
              );
            })}
            {!shown.length && <tr><td colSpan={columns.length + 1} className="safety-muted">Nothing matches the current filters.</td></tr>}
          </tbody>
        </table>
      </div>
      {limit > 0 && ordered.length > limit && (
        <div className="ov-more">Showing the top {limit} of {ordered.length} by the current sort{pins.length ? `, plus ${pins.length} pinned` : ''}.</div>
      )}
    </>
  );
}

export function SummaryTable({ rows, analyses, measure, by, sort, onSort, onRowClick }) {
  const cell = (r, a) => {
    const v = r.byAnalysis[a]?.[measure === 'miles' ? 'miles' : 'sites'] || 0;
    return v ? (measure === 'miles' ? v.toFixed(2) : v) : <span className="ov-faint">–</span>;
  };
  const columns = [
    { key: 'label', label: by === 'road' ? 'Road' : 'Road · segment', sortValue: (r) => [r.road, r.segment || 0], render: (r) => r.label },
    { key: 'ranges', label: 'Mileposts', render: (r) => <span className="safety-muted">{r.ranges.map(([a, b]) => `${a.toFixed(1)}–${b.toFixed(1)}`).join(', ')}</span> },
    { key: 'miles', label: 'Surveyed mi', num: true, title: 'Directional miles: each surveyed direction counts', sortValue: (r) => r.miles, render: (r) => r.miles.toFixed(1) },
    ...analyses.map((a) => ({
      key: a, label: a, num: true, title: ANALYSIS_NAMES[a],
      sortValue: (r) => r.byAnalysis[a]?.[measure === 'miles' ? 'miles' : 'sites'] || 0, render: (r) => cell(r, a),
    })),
    { key: 'total', label: measure === 'miles' ? 'Deficient mi' : 'Sites', num: true,
      sortValue: (r) => (measure === 'miles' ? r.deficientMi : r.sites),
      render: (r) => <span className="safety-key">{measure === 'miles' ? r.deficientMi.toFixed(2) : r.sites}</span> },
    { key: 'severe', label: 'Severe', num: true, sortValue: (r) => r.severe, render: (r) => r.severe },
    { key: 'pct', label: '% deficient', num: true, title: 'Deficient length over surveyed directional miles; overlapping sites count once',
      sortValue: (r) => r.pct, render: (r) => `${r.pct.toFixed(1)}%` },
  ];
  return <SortableTable columns={columns} rows={rows} sort={sort} onSort={onSort} onRowClick={onRowClick} limit={0} rowKey={(r) => r.key} />;
}

export function ClustersTable({ rows, position, axis, unify, sort, onSort, onRowClick, limit }) {
  const columns = [
    { key: 'road', label: unify ? 'Road' : 'Road · segment', sortValue: (c) => [c.road, c.segment || 0], render: (c) => (unify ? c.road : segmentName(c)) },
    { key: 'position', label: axis === 'mi' ? 'Mileage' : 'Milepost', num: true, sortValue: (c) => position(c.segKey, c.mpStart),
      render: (c) => (axis === 'mi' ? `${fmt2(position(c.segKey, c.mpStart))} mi` : `MP ${fmt2(c.mpStart)}`) },
    { key: 'spanMi', label: 'Span (mi)', num: true, sortValue: (c) => c.spanMi, render: (c) => c.spanMi.toFixed(2) },
    { key: 'n', label: 'Analyses', num: true, sortValue: (c) => [c.n, SEVERITY_RANK[c.severity] || 0, c.sites], render: (c) => <span className="safety-key">{c.n}</span> },
    { key: 'which', label: 'Which', render: (c) => c.analyses.map((a) => <AnalysisTag key={a} analysis={a} />) },
    { key: 'sites', label: 'Sites', num: true, sortValue: (c) => c.sites, render: (c) => c.sites },
    { key: 'severity', label: 'Worst severity', sortValue: (c) => SEVERITY_RANK[c.severity] || 0, render: (c) => <Severity value={c.severity} /> },
    { key: 'reports', label: 'Open report at this milepost',
      render: (c) => (Object.keys(c.reports).length ? (
        <span className="ov-report-row">
          {Object.entries(c.reports).map(([a, site]) => (
            <ReportLink key={a} url={site.reportUrl} label={a} title={`Open the ${a} report at MP ${fmt2(site.mpStart)}`} />
          ))}
        </span>
      ) : <span className="safety-muted">—</span>) },
  ];
  return <SortableTable columns={columns} rows={rows} sort={sort} onSort={onSort} onRowClick={onRowClick} limit={limit} rowKey={(c) => c.id} />;
}

export function SitesTable({ rows, position, axis, sort, onSort, onRowClick, limit }) {
  const columns = [
    { key: 'road', label: 'Road', sortValue: (r) => [r.road, r.segment || 0], render: (r) => r.road },
    { key: 'segment', label: 'Segment', sortValue: (r) => r.segment || 0, render: (r) => r.segment || '—' },
    { key: 'analysis', label: 'Analysis', sortValue: (r) => ANALYSES.indexOf(r.analysis), render: (r) => <AnalysisTag analysis={r.analysis} /> },
    { key: 'direction', label: 'Dir', title: 'P = positive (increasing milepost), N = negative', sortValue: (r) => r.direction, render: (r) => r.direction },
    { key: 'position', label: axis === 'mi' ? 'Mileage' : 'Milepost', num: true, sortValue: (r) => position(r.segKey, r.mpStart),
      render: (r) => (axis === 'mi' ? `${fmt2(position(r.segKey, r.mpStart))} mi` : `MP ${fmt2(r.mpStart)}`) },
    { key: 'lengthMi', label: 'Length (mi)', num: true, sortValue: (r) => r.lengthMi || 0, render: (r) => (r.lengthMi ? r.lengthMi.toFixed(2) : '—') },
    { key: 'severity', label: 'Severity', sortValue: (r) => SEVERITY_RANK[r.severity] || 0, render: (r) => <Severity value={r.severity} /> },
    { key: 'worstText', label: 'Worst value', sortValue: (r) => r.worstText || '', render: (r) => r.worstText || '—' },
    { key: 'projectId', label: 'Project', num: true, sortValue: (r) => r.projectId, render: (r) => `#${r.projectId}` },
    { key: 'report', label: 'Report', title: 'ISD: one report per maneuver that is short of the required distance, worst first',
      render: (r) => {
        const short = shortManeuvers(r.maneuvers).filter((m) => m.reportUrl);
        if (!short.length) return <ReportLink url={r.reportUrl} label="Report" title={`Open the ${r.analysis} report at MP ${fmt2(r.mpStart)}`} />;
        return (
          <span className="ov-report-row">
            {short.map((m) => <ReportLink key={m.id} url={m.reportUrl} label={`${m.name} ${pct(m.quality)}`} title={`${m.id} · ${m.label || ''}`} />)}
          </span>
        );
      } },
  ];
  return <SortableTable columns={columns} rows={rows} sort={sort} onSort={onSort} onRowClick={onRowClick} limit={limit} rowKey={(r) => r.id} />;
}
